import { useEffect, useRef, useState } from "react";
import mapboxgl from "mapbox-gl";
import { mapboxAccessToken, mapboxSatelliteStyle, mapStyle } from "../map/config";
import { prefersImageInNewTab, type ExportableAssignment } from "./exportAssignmentImages";
import "./assignment-map-modal.css";

const SOURCE_ID = "assignment-area";
const FILL_LAYER_ID = "assignment-area-fill";
const LINE_LAYER_ID = "assignment-area-line";

const toFeatureCollection = (assignment: ExportableAssignment): GeoJSON.FeatureCollection => ({
    type: "FeatureCollection",
    features: [
        {
            type: "Feature",
            properties: { name: assignment.name },
            geometry: {
                type: "Polygon",
                coordinates: assignment.area?.coordinates ?? []
            }
        }
    ]
});

const areaBounds = (assignment: ExportableAssignment): mapboxgl.LngLatBounds | null => {
    const rings = assignment.area?.coordinates;
    if (!rings?.length) {
        return null;
    }

    const bounds = new mapboxgl.LngLatBounds();
    for (const ring of rings) {
        for (const position of ring) {
            bounds.extend([position[0], position[1]]);
        }
    }
    return bounds.isEmpty() ? null : bounds;
};

interface AssignmentMapModalProps {
    assignment: ExportableAssignment;
    onClose: () => void;
    /** Visas som knapp i sidfoten när sidan även kan ladda ned kartan som bild. */
    onDownload?: () => void;
    isDownloading?: boolean;
}

/**
 * Uppdraget på en riktig karta: att ladda ned en PNG fungerar dåligt på telefonen, där bilden
 * hamnar i galleriet i stället för att öppnas. Här kan man i stället panorera, zooma och se sin
 * egen position i förhållande till området.
 */
export const AssignmentMapModal = ({
    assignment,
    onClose,
    onDownload,
    isDownloading = false
}: AssignmentMapModalProps): JSX.Element => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const mapRef = useRef<mapboxgl.Map | null>(null);
    const [isSatellite, setIsSatellite] = useState(false);

    // Esc stänger — samma förväntan som i övriga dialoger.
    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                onClose();
            }
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [onClose]);

    useEffect(() => {
        if (!containerRef.current || !mapboxAccessToken) {
            return;
        }

        mapboxgl.accessToken = mapboxAccessToken;
        const map = new mapboxgl.Map({
            container: containerRef.current,
            style: mapStyle,
            center: [0, 0],
            zoom: 1,
            attributionControl: false
        });
        mapRef.current = map;

        map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), "top-right");
        // Utdelaren står ofta mitt i området — egen position gör kartan användbar på plats.
        map.addControl(
            new mapboxgl.GeolocateControl({
                positionOptions: { enableHighAccuracy: true },
                trackUserLocation: true,
                showUserHeading: true
            }),
            "top-right"
        );

        const drawArea = () => {
            if (!map.getSource(SOURCE_ID)) {
                map.addSource(SOURCE_ID, { type: "geojson", data: toFeatureCollection(assignment) });
            }
            if (!map.getLayer(FILL_LAYER_ID)) {
                map.addLayer({
                    id: FILL_LAYER_ID,
                    type: "fill",
                    source: SOURCE_ID,
                    paint: { "fill-color": "#4a7a64", "fill-opacity": 0.25 }
                });
            }
            if (!map.getLayer(LINE_LAYER_ID)) {
                map.addLayer({
                    id: LINE_LAYER_ID,
                    type: "line",
                    source: SOURCE_ID,
                    paint: { "line-color": "#114838", "line-width": 3 }
                });
            }
        };

        // Varje stilbyte (flygfoto ↔ karta) nollställer källor och lager — "style.load" är
        // det enda tillfälle då den nya stilen är klar och ytan kan ritas om överst.
        map.on("style.load", drawArea);

        map.once("load", () => {
            const bounds = areaBounds(assignment);
            if (bounds) {
                map.fitBounds(bounds, { padding: 40, maxZoom: 17, duration: 0 });
            }
        });

        return () => {
            map.remove();
            mapRef.current = null;
        };
    }, [assignment]);

    const handleToggleSatellite = () => {
        const map = mapRef.current;
        if (!map) return;
        const next = !isSatellite;
        setIsSatellite(next);
        map.setStyle(next ? mapboxSatelliteStyle : mapStyle);
    };

    const handleRecenter = () => {
        const bounds = areaBounds(assignment);
        if (bounds) {
            mapRef.current?.fitBounds(bounds, { padding: 40, maxZoom: 17 });
        }
    };

    return (
        <div
            className="assignment-map-modal"
            role="dialog"
            aria-modal="true"
            aria-label={`Karta för ${assignment.name}`}
            onClick={(event) => {
                if (event.target === event.currentTarget) {
                    onClose();
                }
            }}
        >
            <div className="assignment-map-modal__panel">
                <header className="assignment-map-modal__header">
                    <div>
                        <h2 className="assignment-map-modal__title">{assignment.name}</h2>
                        {assignment.targetAddressCount != null && (
                            <p className="assignment-map-modal__meta">
                                {assignment.targetAddressCount} adresser
                            </p>
                        )}
                    </div>
                    <button
                        type="button"
                        className="assignment-map-modal__close"
                        onClick={onClose}
                        aria-label="Stäng kartan"
                    >
                        ✕
                    </button>
                </header>

                <div className="assignment-map-modal__map" ref={containerRef}>
                    {!mapboxAccessToken && (
                        <p className="assignment-map-modal__missing-token">
                            Kartan kunde inte laddas.
                        </p>
                    )}
                </div>

                <footer className="assignment-map-modal__footer">
                    <button type="button" className="assignment-map-modal__action" onClick={handleRecenter}>
                        Centrera
                    </button>
                    <button
                        type="button"
                        className="assignment-map-modal__action"
                        onClick={handleToggleSatellite}
                    >
                        {isSatellite ? "Karta" : "Flygfoto"}
                    </button>
                    {onDownload && (
                        <button
                            type="button"
                            className="assignment-map-modal__action"
                            onClick={onDownload}
                            disabled={isDownloading}
                        >
                            {isDownloading ? "Hämtar…" : prefersImageInNewTab() ? "Visa bild" : "Spara bild"}
                        </button>
                    )}
                </footer>
            </div>
        </div>
    );
};

export default AssignmentMapModal;
